/*
 * File: src/router/page/login/Login.tsx
 * Notes:
 *   > ...
 */

import React, {
  useState,
  useEffect,
  MouseEventHandler,
  VoidFunctionComponent,
} from "react";
import Grid from "@material-ui/core/Grid";
import Button, { ButtonProps } from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Container from "../../../components/page/PageContent";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";
import ProgressButton from "../../../components/button/ProgressButton";
import MuiLink from "@material-ui/core/Link";
import { useSnackbar } from "notistack";
import useTextHighlightStyles from "../../../styles/useTextHighlightStyles";
import ForgotPassword from "../../../components/dialogs/ForgotPassword";
import ForgotUsername from "../../../components/dialogs/ForgotUsername";
import ResendActivationLink from "../../../components/dialogs/ResendActivationLink";
import {
  CUSTOMER_SERVICE_NUMBER,
  GENERIC_ERROR,
} from "../../../constants/strings";
import ActivateOnlineAccess from "../../../components/dialogs/ActivateOnlineAccess";
import RemoveContactDetails from "../../../components/dialogs/RemoveContactDetails";
import { gql, useApolloClient } from "@apollo/client";
import { PageHeaderProps } from "../../../components/page/PageHeader";
import FormikField from "../../../components/inputs/FormikField";
import FormikTextField from "../../../components/inputs/FormikTextField";
import getApolloErrorMessage from "../../../gql/getApolloErrorMessage";

const schema = Yup.object().shape({
  username: Yup.string()
    .required("Please enter your username.")
    .max(100, "Too long."),
  password: Yup.string()
    .required("Please enter your password.")
    .max(40, "Too long."),
});

const ResendActivationLinkMutation = gql`
  mutation ($username: String!) {
    resendActivationLink(username: $username) {
      success
      message
    }
  }
`;

const LoginFrame: VoidFunctionComponent = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [state, setState] = useState({
    loggedIn: false,
    showForgotUsernameDialog: false,
    showForgotPasswordDialog: false,
    showReactivationLinkDialog: false,
    reactivationStep: 0,
    reactivationLoading: false,
    userName: "",
  });

  const handleUsernameClick: MouseEventHandler = (event) => {
    event.preventDefault();
    setState((s) => ({ ...s, showForgotUsernameDialog: true }));
  };

  const handleUsernameClose = () => {
    setState((s) => ({ ...s, showForgotUsernameDialog: false }));
  };

  const handlePasswordClose = () => {
    setState((s) => ({ ...s, showForgotPasswordDialog: false }));
  };

  const handlePasswordClick: MouseEventHandler = (event) => {
    event.preventDefault();
    setState((s) => ({ ...s, showForgotPasswordDialog: true }));
  };

  const handleActivationResend = async () => {
    setState((s) => ({ ...s, reactivationLoading: true }));

    const username = state.userName.trim();

    if (state.reactivationStep === 0) {
      try {
        const { data } = await client.mutate({
          mutation: ResendActivationLinkMutation,
          variables: { username },
        });

        if (data) {
          const { success, message } = data.resendActivationLink;

          if (success) {
            setState((s) => ({
              ...s,
              reactivationLoading: false,
              reactivationStep: 1,
            }));
          } else if (
            !success &&
            message ===
              `Invalid or missing email, please call customer service at ${CUSTOMER_SERVICE_NUMBER}`
          ) {
            enqueueSnackbar(message, { variant: "error" });
            setState((s) => ({ ...s, reactivationLoading: false }));
          }
        }
      } catch (error) {
        enqueueSnackbar(getApolloErrorMessage(error), {
          variant: "error",
        });
        setState((s) => ({ ...s, reactivationLoading: false }));
      }
    }
  };

  const closeActivationDialog = () => {
    setState((s) => ({
      ...s,
      showReactivationLinkDialog: false,
      reactivationStep: 0,
    }));
  };

  const openActivationDialog = () => {
    setState((s) => ({ ...s, showReactivationLinkDialog: true }));
  };

  const params = new URLSearchParams(history.location.search);

  const redirect = params.get("redirect");

  return (
    <>
      <Typography variant="h5">Login to CLYNK Online Access</Typography>

      <ForgotPassword
        open={state.showForgotPasswordDialog}
        onClose={handlePasswordClose}
      />
      <ForgotUsername
        open={state.showForgotUsernameDialog}
        onClose={handleUsernameClose}
      />

      <ResendActivationLink
        open={state.showReactivationLinkDialog}
        onClose={closeActivationDialog}
        handleReactivationResend={handleActivationResend}
        reactivationStep={state.reactivationStep}
        reactivationLoading={state.reactivationLoading}
      />

      <Formik
        validationSchema={schema}
        initialValues={{ username: "", password: "" }}
        onSubmit={async (values, actions) => {
          try {
            const res = await axios.post(
              process.env.REACT_APP_LOGIN_URL || "/api/identity/login.php",
              {
                username: values.username.trim().substring(0, 20),
                password: values.password,
              },
              {
                withCredentials: true,
                headers: {
                  "Content-Type": "application/json",
                },
                timeout: 5000,
                validateStatus: (status: number) =>
                  status === 200 || status === 401,
              }
            );

            const { success, message, fields } = res.data;

            if (success) {
              setState((s) => ({ ...s, loggedIn: true }));

              const loginInfo = { origin: "/login", loggedIn: true };

              localStorage.setItem("loginInfo", JSON.stringify(loginInfo));

              if (redirect) {
                window.location.href = `/${redirect}`;
              } else {
                window.location.href = "/dashboard";
              }
            } else {
              if (fields) {
                const status = Object.fromEntries(
                  fields.map((f: { field: string; errors: string[] }) => [
                    f.field,
                    f.errors,
                  ])
                );

                actions.setStatus(status);
              } else if (
                message ===
                "You have not yet activated Online Access. Please check your email for an activation link."
              ) {
                openActivationDialog();
              } else {
                enqueueSnackbar(message || GENERIC_ERROR, { variant: "error" });
              }
            }
          } catch (error) {
            let message = GENERIC_ERROR;

            if (axios.isAxiosError(error)) {
              const errors = error.response?.data?.errors;

              if (errors && errors.length > 0) {
                message = errors[0].message ?? message;
              }
            }

            enqueueSnackbar(message, {
              variant: "error",
            });
          }
        }}
      >
        {(props) => {
          return (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                // TODO: if props.isSubmitting skip & verify this doesn't allow a double submit
                if (!props.isSubmitting) {
                  props.submitForm();
                }
              }}
              style={{
                marginTop: 32,
                width: 300,
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <FormikField
                name="username"
                label="Username"
                component={FormikTextField}
                InputProps={{ name: "username" }}
              />

              <MuiLink
                style={{ display: "block", marginBottom: 32 }}
                href="#"
                onClick={handleUsernameClick}
              >
                Forgot your username?
              </MuiLink>

              <FormikField
                name="password"
                label="Password"
                component={FormikTextField}
                InputProps={{
                  type: "password",
                }}
              />

              <MuiLink
                style={{ display: "block", marginBottom: 32 }}
                href="#"
                onClick={handlePasswordClick}
              >
                Forgot your password?
              </MuiLink>

              <ProgressButton
                type="submit"
                variant="contained"
                color="primary"
                style={{ marginTop: 16, marginBottom: 16 }}
                loading={props.isSubmitting || state.loggedIn}
                disabled={props.isSubmitting || state.loggedIn}
                LinearProgressProps={{ color: "secondary" }}
                onClick={() => {
                  setState((s) => ({ ...s, userName: props.values.username }));
                }}
              >
                Login
              </ProgressButton>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

function MyButton<C>(props: ButtonProps & { to?: string; component?: C }) {
  const { children, style, ...rest } = props;

  return (
    <Button
      color="primary"
      variant="contained"
      style={{ marginTop: 16, ...style }}
      {...rest}
    >
      {children}
    </Button>
  );
}

function SignUpFrame() {
  return (
    <>
      <Typography variant="h5">Set up CLYNK Online Access</Typography>

      <Typography variant="h6" style={{ marginTop: 32 }}>
        With CLYNK Online Access, you can:
      </Typography>

      <ul>
        <li>View your balance from anywhere</li>
        <li>Set donation preferences</li>
        <li>Edit account detail</li>
      </ul>

      <MyButton component={Link} to="/register/enter-card">
        Get Started
      </MyButton>
    </>
  );
}

export interface LoginProps {
  setState: (value: {
    hero: { title: string; color: PageHeaderProps["color"] };
  }) => void;
}

const Login: VoidFunctionComponent<LoginProps> = ({ setState }) => {
  useEffect(() => {
    document.title = "Login - Clynk";

    setState({
      hero: {
        title: "CLYNK Online Access",
        color: "secondary",
      },
    });
  }, [setState]);

  const history = useHistory();
  const match = useRouteMatch<{ path: string }>();
  const textClasses = useTextHighlightStyles();

  const handleClose = () => {
    history.replace({ pathname: "/login" });
  };

  const activate = match?.params.path === "activate";
  const removeContactDetails = match?.params.path === "remove-contact-details";

  return (
    <>
      <Container maxWidth="lg">
        <div style={{ marginBottom: 32 }}>
          <Typography>
            Welcome CLYNKer. We are glad you are here!
            <br />
            <br />
            If you signed up for a CLYNK account online at clynk.com, you can
            log-in with your username and password in the form to the left. If
            you signed up at the kiosk, you will need to establish Online Access
            by clicking the{" "}
            <span className={textClasses.textHighlightGreen}>
              Get Started
            </span>{" "}
            button below.
          </Typography>
        </div>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <LoginFrame />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SignUpFrame />
          </Grid>
        </Grid>
      </Container>

      <ActivateOnlineAccess open={activate} onClose={handleClose} />
      <RemoveContactDetails open={removeContactDetails} onClose={handleClose} />
    </>
  );
};

export default Login;
